import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AccountService } from '../account-service.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-verify-email',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  template: `
    <div class="verify-email-container">
      <h3>{{ 'verify-email.TITLE' | translate }}</h3>

      <input
        type="text"
        [(ngModel)]="verificationCode"
        [attr.placeholder]="'verify-email.ENTER_CODE' | translate"
        [disabled]="isExpired"
      />
      <button (click)="sendVerificationCode()" [disabled]="isExpired">
        {{ 'verify-email.VERIFY_BUTTON' | translate }}
      </button>
      <p *ngIf="message">{{ message }}</p>

      <div *ngIf="isExpired">
        <p>{{ 'verify-email.CODE_EXPIRED' | translate }}</p>
        <button (click)="generateNewCode()" [disabled]="isGenerating">
          {{ 'verify-email.GENERATE_NEW_CODE' | translate }}
        </button>
        <p *ngIf="countdown > 0">
          {{
            'verify-email.RETRY_IN'
              | translate
                : { minutes: countdownMinutes, seconds: countdownSeconds }
          }}
        </p>
      </div>
      <!-- Go Back Button -->
      <button (click)="goBack()">
        {{ 'verify-email.GO_BACK' | translate }}
      </button>
    </div>
  `,
  styleUrl: './verify-email.component.css',
})
export class VerifyEmailComponent {
  verificationCode: string = '';
  message: string = '';
  email: string = '';
  isExpired: boolean = false;
  isGenerating: boolean = false;
  countdown: number = 0; // Countdown in seconds

  constructor(
    private accountService: AccountService,
    private router: Router,
  ) {
    // Retrieve the email from localStorage
    this.email = localStorage.getItem('email') || '';
  }

  get countdownMinutes(): string {
    return Math.floor(this.countdown / 60)
      .toString()
      .padStart(2, '0');
  }

  get countdownSeconds(): string {
    return (this.countdown % 60).toString().padStart(2, '0');
  }

  sendVerificationCode(): void {
    if (!this.email) {
      this.message = $localize`:@@verify-email.ERROR_NO_EMAIL:Error: No email found.`;
      return;
    }

    if (!this.verificationCode.trim()) {
      this.message = $localize`:@@verify-email.ENTER_CODE_MESSAGE:Please enter the verification code.`;
      return;
    }

    this.accountService
      .verifyEmail(this.email, this.verificationCode)
      .subscribe({
        next: (response) => {
          if (response.verified && !response.expired) {
            this.router.navigate(['available-clans']);
          } else if (response.expired) {
            this.isExpired = true;
            this.message = $localize`:@@verify-email.CODE_EXPIRED_MESSAGE:Verification code has expired.`;
          } else {
            this.message = $localize`:@@verify-email.INVALID_CODE_MESSAGE:Invalid verification code. Please try again.`;
          }
        },
        error: () => {
          this.message = $localize`:@@verify-email.VERIFICATION_FAILED_MESSAGE:Verification failed. Please check your code and try again.`;
        },
      });
  }

  generateNewCode(): void {
    this.isGenerating = true;
    this.accountService.generateVerificationCode(this.email).subscribe({
      next: () => {
        this.isGenerating = true;
        this.isExpired = false;
        this.countdown = 600; // Set countdown to 10 minutes (600 seconds)
        this.startCountdown();
        this.message = $localize`:@@verify-email.NEW_CODE_SENT:New verification code sent!`;
      },
      error: () => {
        this.isGenerating = false;
        this.message = $localize`:@@verify-email.GENERATE_CODE_FAILED:Failed to generate new verification code. Please try again.`;
      },
    });
  }
  goBack(): void {
    this.router.navigate(['login-page']);
  }
  startCountdown(): void {
    const interval = setInterval(() => {
      if (this.countdown > 0) {
        this.countdown--;
      } else {
        clearInterval(interval);
        this.isExpired = false;
      }
    }, 1000);
  }
}
